import Button from '@mui/material/Button';
import React, { FC, useCallback, useContext, useEffect, useRef } from 'react';
import { GuestRoutes } from '../../routers/GuestRouter';
import { NotificationContext } from '../../contexts/NotificationContext';
import { useForm } from 'react-hook-form';
import { useEnumState } from '../../hooks/useEnumState';
import { useApiErrorHandler } from '../../hooks/useApiErrorHandler';
import { PasswordInputController } from '../InputControllers/PasswordInputController';
import { routeWithQuery } from '../../helpers/routeWithQuery';
import { AuthGoBack } from '../AuthDialog/AuthGoBack';
import { AuthDialog } from '../AuthDialog/AuthDialog';
import { useTranslation } from 'react-i18next';
import { apiAuth } from '../../resources/api/apiAuth';
import Grid from '@mui/material/Grid';
import { TextInputController } from '../InputControllers/TextInputController';

const { useSignUp } = apiAuth;

export const AuthSignUp: FC = () => {
  const { addNotification } = useContext(NotificationContext);
  const apiErrorHandler = useApiErrorHandler();
  const form = useForm();
  const { t } = useTranslation();

  const { execute: signUp } = useSignUp();

  const [done, setDone] = useEnumState(false, true);
  const passwordRef = useRef('');
  passwordRef.current = form.watch('password', '');

  const onSubmit = useCallback(async (data) => {
    try {
      await signUp({
        first_name: data.firstName,
        last_name: data.lastName,
        email: data.email,
        password: data.password,
        phone_number: data.phoneNumber || void 0,
      });
      addNotification({
        message: t('auth.signUp.success'),
        severity: 'success',
      });
      setDone();
    } catch (e) {
      apiErrorHandler(e);
    }
  }, []);

  useEffect(() => {
    form.reset({
      first_name: '',
      last_name: '',
      email: '',
      password: '',
      password2: '',
      phone_number: '',
    });
  }, []);

  return (
    <AuthDialog
      title={done ? t('auth.signUp.done') : t('auth.signUp.encouragement')}
      onSubmit={form.handleSubmit(onSubmit)}
      actions={
        done ? (
          <AuthGoBack to={routeWithQuery(GuestRoutes.SignIn, [], { email: form.getValues().email })} />
        ) : (
          <Button variant="contained" color="primary" type="submit">
            {t('auth.signUp.submitButton')}
          </Button>
        )
      }
    >
      {!done ? (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextInputController
              control={form.control}
              errors={form.formState.errors}
              label={t('common.formLabel.email')}
              name={'email'}
              rules={{ required: true }}
              InputProps={{
                autoComplete: 'email',
              }}
            />
          </Grid>

          <Grid item xs={6}>
            <TextInputController
              control={form.control}
              errors={form.formState.errors}
              label={t('common.formLabel.firstName')}
              name={'firstName'}
              rules={{ required: true }}
              InputProps={{
                autoComplete: 'given-name',
              }}
            />
          </Grid>

          <Grid item xs={6}>
            <TextInputController
              control={form.control}
              errors={form.formState.errors}
              label={t('common.formLabel.lastName')}
              name={'lastName'}
              rules={{ required: true }}
              InputProps={{
                autoComplete: 'family-name',
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <PasswordInputController
              control={form.control}
              errors={form.formState.errors}
              label={t('auth.inputs.password.label')}
              name="password"
              rules={{ required: true }}
              InputProps={{
                inputProps: {
                  autoComplete: 'new-password',
                  tabIndex: 1,
                },
                autoComplete: 'new-password',
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <PasswordInputController
              control={form.control}
              errors={form.formState.errors}
              label={t('auth.inputs.confirmPassword.label')}
              name="password2"
              rules={{
                required: true,
                validate: (value) => {
                  const msg = t('auth.inputs.confirmPassword.validation.notMatch');
                  return value === passwordRef.current || msg;
                },
              }}
              InputProps={{
                inputProps: {
                  autoComplete: 'new-password',
                  tabIndex: 2,
                },
                autoComplete: 'new-password',
              }}
            />
          </Grid>
        </Grid>
      ) : null}
    </AuthDialog>
  );
};
